/* The 30-second Cornerstone Ops commercial — a separate film from the welcome tour.
   Everything on screen is a pure function of t (seconds): no CSS animations, no transitions, no
   timers. That makes the offline render deterministic — the renderer injects the exact frame time
   and screenshots the stage. */
import { BrandBadge } from "@/components/BrandMark";

const SHOT = "/tour/shots";
const SHOT_V = "14";
const NAVY = "#0B1330";
const GOLD = "#d4af37";

const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const seg = (v, a, b) => {
  const x = clamp((v - a) / Math.max(b - a, 0.0001));
  return x * x * (3 - 2 * x);
};

/* A product screenshot as a backdrop: a blurred, darkened copy fills the frame, the real
   screenshot sits inside it complete. Cropping a 16:9 screenshot to a square feed post zoomed
   it to the point where a single word filled the screen, so nothing is cropped any more. */
const Shot = ({ name, p = 0, dim = 0.55, fit = "contain", lift = 0 }) => (
  <div className="absolute inset-0 overflow-hidden">
    <img src={`${SHOT}/${name}.jpg?v=${SHOT_V}`} alt="" aria-hidden
      className="absolute inset-0 h-full w-full object-cover"
      style={{ filter: "blur(26px) saturate(0.6) brightness(0.45)", transform: "scale(1.2)" }} />
    <img src={`${SHOT}/${name}.jpg?v=${SHOT_V}`} alt="" aria-hidden
      className={`absolute inset-0 h-full w-full ${fit === "cover" ? "object-cover" : "object-contain"}`}
      style={{ transform: `scale(${(fit === "cover" ? 1.05 : 0.98) + p * 0.05}) translateY(${lift}%)`,
               filter: "saturate(0.95) contrast(1.05)" }} />
    <div className="absolute inset-0" style={{ background: `rgba(9,15,40,${dim})` }} />
    <div className="absolute inset-0" style={{ background:
      "radial-gradient(120% 95% at 50% 45%, transparent 22%, rgba(7,12,30,0.88) 100%)" }} />
  </div>
);

const Kicker = ({ children, u, o = 1 }) => (
  <p style={{ color: GOLD, opacity: o, fontSize: `${1.9 * u}px`, fontWeight: 700,
              letterSpacing: `${0.55 * u}px`, textTransform: "uppercase" }}>{children}</p>
);

/* ---- 1. the hook ---------------------------------------------------------------------------- */
const NOISE = [
  "cleaner running late", "owner wants an update", "invoice still unpaid",
  "who has the gate code?", "was the turnover done?", "double-booked Saturday",
  "photos are in my texts", "vendor can't find the lockbox",
];

const Hook = ({ p, u }) => {
  const settle = seg(p, 0.46, 0.72);
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: NAVY }}>
      <Shot name="messages" dim={0.8} p={p} />
      {NOISE.map((line, i) => {
        const born = 0.02 + i * 0.05;
        const on = seg(p, born, born + 0.1) * (1 - settle);
        // The middle third is reserved for the headline, so the clutter lives above and below it.
        const top = i < 4 ? 5 + i * 8 : 66 + (i - 4) * 8;
        return (
          <p key={line} style={{
            position: "absolute", left: `${i % 2 ? 50 : 6}%`, top: `${top}%`,
            opacity: on * 0.9, transform: `translateY(${(1 - on) * 2.4 * u}px)`,
            color: "rgba(226,232,255,0.94)", fontSize: `${2.05 * u}px`, fontWeight: 600,
            background: "rgba(10,16,40,0.62)", border: "1px solid rgba(255,255,255,0.09)",
            borderRadius: `${1.1 * u}px`, padding: `${0.8 * u}px ${1.5 * u}px`, maxWidth: "44%",
          }}>{line}</p>
        );
      })}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center"
           style={{ padding: `${8 * u}px`, background: `rgba(11,19,48,${settle * 0.8})` }}>
        <div style={{ opacity: settle, transform: `translateY(${(1 - settle) * 3 * u}px)` }}>
          <Kicker u={u}>Property operations</Kicker>
          <h1 className="font-display" style={{
            marginTop: `${2.4 * u}px`, color: "#fff", fontWeight: 800,
            fontSize: `${7.8 * u}px`, lineHeight: 1.03, letterSpacing: `${-0.1 * u}px` }}>
            Your operation<br />shouldn't be<br />
            <span style={{ color: GOLD }}>running you.</span>
          </h1>
        </div>
      </div>
    </div>
  );
};

/* ---- 2. everything it holds ----------------------------------------------------------------- */
const TILES = [
  ["properties", "Properties"], ["team", "Teams"], ["inspections", "Inspections"],
  ["reports", "Owners"], ["vendors", "Vendors"], ["invoices", "Payments"],
];

const Stack = ({ p, u, wide, tall }) => (
  <div className="absolute inset-0 overflow-hidden" style={{ background: NAVY }}>
    <div className="absolute inset-0" style={{ background:
      "radial-gradient(100% 80% at 50% 0%, rgba(38,62,140,0.5), rgba(11,19,48,1) 70%)" }} />
    <div className="absolute inset-0 flex flex-col justify-center"
         style={{ padding: "5%" }}>
      <Kicker u={u} o={seg(p, 0, 0.12)}>One system</Kicker>
      <div style={{ display: "grid", gap: `${1.3 * u}px`, marginTop: `${2 * u}px`,
                    gridTemplateColumns: wide ? "repeat(3, 1fr)" : "repeat(2, 1fr)" }}>
        {TILES.map(([shot, label], i) => {
          const on = seg(p, 0.08 + i * 0.1, 0.24 + i * 0.1);
          return (
            <div key={label} style={{
              position: "relative", overflow: "hidden", borderRadius: `${1.3 * u}px`,
              border: `1px solid rgba(212,175,55,${0.18 + on * 0.35})`,
              aspectRatio: wide ? "16 / 10" : "16 / 11",
              opacity: 0.12 + on * 0.88,
              transform: `translateY(${(1 - on) * 2.6 * u}px) scale(${0.965 + on * 0.035})`,
            }}>
              <img src={`${SHOT}/${shot}.jpg?v=${SHOT_V}`} alt="" aria-hidden
                className="h-full w-full object-cover"
                style={{ filter: `saturate(${0.5 + on * 0.5}) brightness(${0.55 + on * 0.45})` }} />
              <div className="absolute inset-0" style={{ background:
                "linear-gradient(to top, rgba(8,13,33,0.92) 12%, rgba(8,13,33,0.1) 70%)" }} />
              <p className="font-display" style={{
                position: "absolute", left: `${1.4 * u}px`, bottom: `${1.1 * u}px`,
                color: "#fff", fontWeight: 700, fontSize: `${2.5 * u}px`, opacity: on }}>
                {label}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

/* ---- 3. the part nobody else does ----------------------------------------------------------- */
const Smart = ({ p, u }) => (
  <div className="absolute inset-0 overflow-hidden" style={{ background: NAVY }}>
    <Shot name="briefing" dim={0.74} p={p} lift={-6} />
    {/* A solid gradient floor under the type: the briefing screen is dense text, and white
        headlines sitting straight on it were unreadable. */}
    <div className="absolute inset-0" style={{ background:
      "linear-gradient(to top, rgba(8,13,33,0.98) 42%, rgba(8,13,33,0.6) 66%, rgba(8,13,33,0.25) 100%)" }} />
    <div className="absolute inset-x-0 bottom-0 flex flex-col"
         style={{ padding: "8%" }}>
      <Kicker u={u} o={seg(p, 0, 0.14)}>Automations · Weather intelligence</Kicker>
      <h2 className="font-display" style={{
        marginTop: `${2 * u}px`, color: "#fff", fontWeight: 800, lineHeight: 1.06,
        fontSize: `${5.6 * u}px`, opacity: seg(p, 0.06, 0.28),
        transform: `translateY(${(1 - seg(p, 0.06, 0.28)) * 2.6 * u}px)` }}>
        It knows what's coming —<br />
        <span style={{ color: GOLD }}>and what to do about it.</span>
      </h2>
      <div style={{ display: "flex", gap: `${1.2 * u}px`, marginTop: `${2.4 * u}px`, flexWrap: "wrap" }}>
        {["Turnovers scheduled", "Crews notified", "Invoices drafted"].map((chip, i) => {
          const on = seg(p, 0.34 + i * 0.13, 0.52 + i * 0.13);
          return (
            <span key={chip} style={{
              opacity: on, transform: `translateY(${(1 - on) * 1.8 * u}px)`,
              color: "#fff", fontWeight: 700, fontSize: `${2 * u}px`,
              borderRadius: `${9 * u}px`, padding: `${1 * u}px ${2 * u}px`,
              background: "rgba(212,175,55,0.16)", border: `1px solid rgba(212,175,55,0.5)` }}>
              {chip}
            </span>
          );
        })}
      </div>
    </div>
  </div>
);

/* ---- 4. one platform ------------------------------------------------------------------------ */
const One = ({ p, u }) => (
  <div className="absolute inset-0 overflow-hidden" style={{ background: NAVY }}>
    <Shot name="dashboard" dim={0.5} p={p} />
    <div className="absolute inset-0" style={{ background:
      "radial-gradient(70% 34% at 50% 50%, rgba(8,13,33,0.92), rgba(8,13,33,0.25) 100%)" }} />
    <div className="absolute inset-0 flex items-center justify-center text-center"
         style={{ padding: "8%", background: `rgba(11,19,48,${0.32 + seg(p, 0.5, 1) * 0.3})` }}>
      <h2 className="font-display" style={{
        color: "#fff", fontWeight: 800, fontSize: `${7.4 * u}px`, lineHeight: 1.05,
        opacity: seg(p, 0.05, 0.3), transform: `scale(${0.96 + seg(p, 0.05, 0.4) * 0.04})` }}>
        One powerful<br /><span style={{ color: GOLD }}>platform.</span>
      </h2>
    </div>
  </div>
);

/* ---- 5. the brand --------------------------------------------------------------------------- */
const Brand = ({ p, u }) => {
  const on = seg(p, 0.04, 0.34);
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center"
         style={{ background: NAVY, padding: "8%" }}>
      <div className="absolute inset-0" style={{ background:
        "radial-gradient(75% 55% at 50% 45%, rgba(46,74,168,0.55), rgba(11,19,48,1) 72%)",
        opacity: 0.5 + on * 0.5 }} />
      <div style={{ position: "relative", opacity: on,
                    transform: `translateY(${(1 - on) * 2.4 * u}px)` }}>
        <div className="flex justify-center">
          <div style={{ width: `${11 * u}px`, height: `${11 * u}px` }}>
            <BrandBadge size="h-full w-full" icon="h-3/5 w-3/5" />
          </div>
        </div>
        <h2 className="font-display" style={{
          marginTop: `${2.6 * u}px`, color: "#fff", fontWeight: 800, fontSize: `${8 * u}px`,
          lineHeight: 1 }}>
          Cornerstone Ops
        </h2>
        <div style={{ height: 1, width: `${26 * u}px`, margin: `${2.4 * u}px auto`,
                      background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
                      opacity: seg(p, 0.2, 0.5) }} />
        <p style={{ color: GOLD, fontSize: `${2.3 * u}px`, fontWeight: 700,
                    letterSpacing: `${0.7 * u}px`, textTransform: "uppercase",
                    opacity: seg(p, 0.26, 0.6) }}>
          The Operational Command Center
        </p>
      </div>
    </div>
  );
};

/* ---- 6. the call to action ------------------------------------------------------------------ */
const Cta = ({ p, u }) => {
  const on = seg(p, 0.02, 0.3);
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center"
         style={{ background: NAVY, padding: "8%" }}>
      <div className="absolute inset-0" style={{ background:
        "radial-gradient(85% 60% at 50% 50%, rgba(46,74,168,0.42), rgba(11,19,48,1) 74%)" }} />
      <div style={{ position: "relative", opacity: on,
                    transform: `translateY(${(1 - on) * 2.2 * u}px)` }}>
        <Kicker u={u}>See what your operation could look like</Kicker>
        <p className="font-display" style={{
          marginTop: `${2.4 * u}px`, color: "#fff", fontWeight: 800, fontSize: `${6.6 * u}px`,
          letterSpacing: `${-0.05 * u}px` }}>
          www.cornerstoneops.io
        </p>
        <div style={{
          display: "inline-block", marginTop: `${3 * u}px`, background: GOLD,
          color: "#101a3d", fontWeight: 800, fontSize: `${2.6 * u}px`,
          padding: `${1.5 * u}px ${3.4 * u}px`, borderRadius: `${9 * u}px`,
          opacity: seg(p, 0.3, 0.62), transform: `scale(${0.94 + seg(p, 0.3, 0.7) * 0.06})` }}>
          14 days free — no card charged today
        </div>
      </div>
    </div>
  );
};

/* ---- 7. the close + the verse --------------------------------------------------------------- */
const Close = ({ p, u }) => {
  const a = seg(p, 0.02, 0.26);
  const b = seg(p, 0.3, 0.56);
  const verse = seg(p, 0.6, 0.86);
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center"
         style={{ background: NAVY, padding: "8%" }}>
      <div className="absolute inset-0" style={{ background:
        "radial-gradient(90% 70% at 50% 40%, rgba(38,62,140,0.5), rgba(9,15,40,1) 76%)" }} />
      <div style={{ position: "relative" }}>
        <h2 className="font-display" style={{
          color: "rgba(226,232,255,0.92)", fontWeight: 800, fontSize: `${5.8 * u}px`,
          lineHeight: 1.1, opacity: a, transform: `translateY(${(1 - a) * 2 * u}px)` }}>
          Stop chasing your operation.
        </h2>
        <h2 className="font-display" style={{
          marginTop: `${1.6 * u}px`, color: GOLD, fontWeight: 800, fontSize: `${7 * u}px`,
          lineHeight: 1.06, opacity: b, transform: `translateY(${(1 - b) * 2.2 * u}px)` }}>
          Start commanding it.
        </h2>
      </div>
      <div style={{ position: "absolute", left: 0, right: 0, bottom: `${7 * u}px`,
                    opacity: verse, padding: "0 8%" }}>
        <div style={{ height: 1, width: `${20 * u}px`, margin: `0 auto ${1.8 * u}px`,
                      background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />
        <p style={{ color: GOLD, fontSize: `${1.7 * u}px`, fontWeight: 700,
                    letterSpacing: `${0.5 * u}px`, textTransform: "uppercase" }}>
          Psalm 24:1, NLT
        </p>
        <p style={{ marginTop: `${1 * u}px`, color: "rgba(226,232,255,0.85)",
                    fontSize: `${2.2 * u}px`, fontStyle: "italic" }}>
          "The earth is the LORD'S, and everything in it."
        </p>
        <p style={{ marginTop: `${1.4 * u}px`, color: "rgba(226,232,255,0.55)",
                    fontSize: `${1.6 * u}px`, fontWeight: 600 }}>
          A Christian-family owned company · Outer Banks, North Carolina
        </p>
      </div>
    </div>
  );
};

export const AD_SCENES = { hook: Hook, stack: Stack, smart: Smart, one: One, brand: Brand,
                           cta: Cta, close: Close };

/* Every scene's own type carries the spoken line, so there is no subtitle strip over the top of
   it — the ad reads with the sound off either way. */
const NO_CAPTION = new Set(["hook", "close", "cta", "brand", "one", "stack", "smart"]);

export default function AdFilm({ t = 0, segments = [], line = "", w = 1080, h = 1080 }) {
  const u = Math.min(w, h) / 100;          // one design unit: type scales with the frame
  const wide = w / h > 1.25;
  const tall = h / w > 1.25;
  const active = segments.findLast?.((s) => t >= s.start)
    || segments.filter((s) => t >= s.start).slice(-1)[0]
    || segments[0];
  if (!active) return <div className="absolute inset-0" style={{ background: NAVY }} />;
  const Scene = AD_SCENES[active.scene] || Hook;
  const p = clamp((t - active.start) / Math.max(active.end - active.start, 0.001));
  const showCaption = line && !NO_CAPTION.has(active.scene);
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: NAVY }}
         data-testid={`ad-scene-${active.scene}`}>
      <Scene p={p} t={t} u={u} w={w} h={h} wide={wide} tall={tall} />
      {showCaption && (
        <div style={{ position: "absolute", left: 0, right: 0, bottom: `${5 * u}px`,
                      display: "flex", justifyContent: "center", padding: "0 6%" }}>
          <p style={{ color: "#fff", fontWeight: 700, fontSize: `${2.4 * u}px`,
                      lineHeight: 1.25, textAlign: "center", maxWidth: "90%",
                      background: "rgba(8,13,33,0.72)", borderRadius: `${1.2 * u}px`,
                      padding: `${1 * u}px ${1.8 * u}px` }}>
            {line}
          </p>
        </div>
      )}
      {/* letterbox-free film grain keeps flat navy from banding in H.264 */}
      <div className="absolute inset-0" style={{ opacity: 0.05, pointerEvents: "none",
        backgroundImage:
          "radial-gradient(rgba(255,255,255,0.35) 1px, transparent 1px)",
        backgroundSize: `${0.6 * u}px ${0.6 * u}px` }} />
    </div>
  );
}
